// Diplomacy screen: your leader vs a selected nation. Agenda cards (verbatim, sourced), the
// relation with its reason, what the two share, how the relation ran since 2000, and a
// grievance ledger in both directions (sanctions, tariffs, wars, strikes, occupations).
//
//   ┌ you ───────────┐ ┌ relation ─────────────┐ ┌ them ──────────┐
//   │ (T) agenda …   │ │  ◉ DENOUNCED          │ │ (X) agenda …   │
//   │  “quote” …     │ │  reason · sources     │ │  “quote” …     │
//   │                │ │  shared memberships   │ │                │
//   │                │ │  ▇▇▇▇▇▇▇▇▇ 2000→now   │ │                │
//   │                │ │  ledger: you → them   │ │                │
//   │                │ │          them → you   │ │                │
//   └────────────────┘ └───────────────────────┘ └────────────────┘

import type { Services } from '../services/api.ts';
import type { War } from '../core/schema.ts';
import type { CountryReport, Grievance, Relation, RelationInfo } from '../world/api.ts';
import { RELATION_COLOR, RELATION_LABEL, RELATION_RANK } from './color.ts';
import { StatementCard } from './CountryPanel.tsx';
import { useServices } from './context.ts';
import { perspectiveDetail, relationHistory, type RelationMonth } from './extensions.ts';
import { fmtDate, fmtMonth, laterThan } from './format.ts';
import { Icon, type IconName } from './icons.tsx';
import { statementBase, uniqueKeys } from './keys.ts';
import { AgendaQuote } from './LeaderSelect.tsx';
import { actorEmblem, actorKind, actorLook, actorName, emblemOf, inOffice, lookOf, reportOf, selfIso, topLeader } from './nation.ts';
import { ClaimLabel, Medallion, Screen, SourceChip, relationIcon } from './parts.tsx';
import { partyOf, relationOf, type Party } from './party.ts';
import { relationRuns, runDates } from './relstrip.ts';

const TAB_OTHERS_MAX = 6;
const TAB_RELATIONS: Relation[] = ['war', 'denounced', 'ally'];

const GRIEVANCE_ICON: Record<Grievance['kind'], IconName> = {
  sanction: 'sanction',
  tariff: 'tariff',
  war: 'war',
  strike: 'disaster',
  occupation: 'control',
};

// How an open-ended grievance reads: measures are in force, conflicts are ongoing.
const STILL_ON: Record<Grievance['kind'], string> = {
  sanction: 'in force',
  tariff: 'in force',
  war: 'ongoing',
  strike: 'ongoing',
  occupation: 'ongoing',
};

/** What the actor table calls a kind of party that has no country report. */
const PARTY_KIND_TEXT: Record<string, string> = { nonstate: 'Non-state party', bloc: 'Bloc' };

/** Latest relation changes listed under the strip, each with its reason. */
const HISTORY_CHANGES_MAX = 4;

export function DiplomacyScreen() {
  const services = useServices();
  const { state, world, actions } = services;
  const self = selfIso(services);
  if (!self) {
    return null;
  }

  const tabs = tabNations(services, self);
  const arg = state.panelArg.value;
  const target = arg && arg !== self ? arg : tabs[0];
  if (target && !tabs.includes(target)) {
    tabs.unshift(target);
  }
  const report = target ? reportOf(world, target) : null;
  // A non-state party has no country report: its column comes from the snapshot instead.
  const party = target && !report ? partyOf(world, state.snapshot.value, target) : null;
  const other = report?.country.name ?? party?.name;
  const close = () => actions.openPanel('none');

  return (
    <Screen
      title="Diplomacy"
      icon="scales"
      class="wc-dip-screen"
      subtitle={other ? `${lookOf(world, self).name} and ${other}` : 'Choose a nation'}
      onClose={close}
    >
      <nav class="wc-dip-tabs">
        {tabs.map((iso3) => (
          <NationTab key={iso3} iso3={iso3} active={iso3 === target} />
        ))}
      </nav>
      {report ? (
        <div class="wc-dip">
          <LeaderColumn iso3={self} side="You" />
          <RelationColumn self={self} report={report} />
          <LeaderColumn iso3={report.country.iso3} side="Them" />
        </div>
      ) : party ? (
        <div class="wc-dip">
          <LeaderColumn iso3={self} side="You" />
          <PartyRelation party={party} />
          <PartyColumn party={party} />
        </div>
      ) : (
        <div class="wc-empty">{target ? `No relation with ${actorName(world, target)} at this date.` : 'No nation selected.'}</div>
      )}
    </Screen>
  );
}

// Other playable leaders first, then wars, denounced and allies from the snapshot.
function tabNations(services: Services, self: string): string[] {
  const { world, state } = services;
  const out = world.perspectives().map((p) => p.iso3).filter((c) => c !== self);
  const snap = state.snapshot.value;
  if (!snap) {
    return out;
  }
  const extra: Array<{ iso3: string; rank: number }> = [];
  for (const [index, info] of snap.relations) {
    if (!TAB_RELATIONS.includes(info.relation)) {
      continue;
    }
    const look = actorLook(world, index);
    if (look && !out.includes(look.iso3) && look.iso3 !== self) {
      extra.push({ iso3: look.iso3, rank: RELATION_RANK[info.relation] });
    }
  }
  extra.sort((a, b) => a.rank - b.rank);
  return [...out, ...extra.slice(0, TAB_OTHERS_MAX).map((e) => e.iso3)];
}

function NationTab({ iso3, active }: { iso3: string; active: boolean }) {
  const { world, state, actions } = useServices();
  const rel = (reportOf(world, iso3)?.relation ?? relationOf(world, state.snapshot.value, iso3))?.relation;
  return (
    <button type="button" class="wc-dip-tab" aria-pressed={active} onClick={() => actions.openPanel('diplomacy', iso3)}>
      <Medallion {...actorEmblem(world, iso3)} size={30} relation={rel} />
      <span>{actorName(world, iso3)}</span>
    </button>
  );
}

function LeaderColumn({ iso3, side }: { iso3: string; side: 'You' | 'Them' }) {
  const { world, state } = useServices();
  const date = state.date.value;
  const look = lookOf(world, iso3);
  const report = reportOf(world, iso3);
  const persp = world.perspectives().find((p) => p.iso3 === iso3);
  const detail = persp ? perspectiveDetail(world, persp.id) : null;

  // The perspective's leader when in office at the date; otherwise whoever held office then.
  const holder = persp && inOffice(world, persp) ? null : topLeader(world, iso3);
  const name = holder ? holder.name : persp ? persp.name : '—';
  let role = persp ? persp.title : 'No sourced leader';
  if (holder) {
    role = `since ${fmtDate(holder.since)}`;
  }

  // Repeated titles and same-day statements are real data: key each card apart.
  const agendaKeys = uniqueKeys((detail?.agenda ?? []).map((a) => a.title + a.date));
  const statementKeys = uniqueKeys((report?.statements ?? []).map(statementBase));

  return (
    <section class={`wc-dip-col wc-dip-col--${side.toLowerCase()}`}>
      <header class="wc-dip-leader">
        <Medallion {...emblemOf(world, iso3, 'person')} size={78} />
        <div>
          <div class="wc-dip-side">{side}</div>
          <div class="wc-dip-nation">{look.name}</div>
          <div class="wc-dip-name">{name}</div>
          <div class="wc-dip-role">
            {role}
            {holder ? <SourceChip ids={holder.sources} /> : null}
          </div>
        </div>
      </header>

      <div class="wc-dip-col-body wc-scroll wc-fade">
        {detail ? (
          <>
            <h3 class="wc-rule-title">{persp && holder ? `${persp.name}: agenda` : 'Agenda'}</h3>
            {laterThan(detail.agenda, date) ? <div class="wc-country-note wc-year">Statements made after this date, dated on each card.</div> : null}
            {detail.agenda.length === 0 ? <div class="wc-empty">No sourced agenda items</div> : null}
            {detail.agenda.map((a, i) => (
              <AgendaQuote key={agendaKeys[i]} item={a} />
            ))}
          </>
        ) : (
          <>
            <h3 class="wc-rule-title">Statements on wars</h3>
            {!report || report.statements.length === 0 ? <div class="wc-empty">No sourced statements</div> : null}
            {report?.statements.map((s, i) => (
              <StatementCard key={statementKeys[i]} s={s} />
            ))}
          </>
        )}
      </div>
    </section>
  );
}

function RelationColumn({ self, report }: { self: string; report: CountryReport }) {
  const { world, state } = useServices();
  const rel = report.relation;
  const other = report.country.iso3;
  const wars = report.wars.filter((w) => (w.sides.a.includes(self) && w.sides.b.includes(other)) || (w.sides.b.includes(self) && w.sides.a.includes(other)));
  // Only what had begun by the date shown, whatever the service passes through.
  const date = state.date.value;
  const ledger = Array.isArray(report.grievances) ? report.grievances.filter((g) => g.from <= date) : null;
  const byMe = ledger?.filter((g) => g.by === self) ?? [];
  const byThem = ledger?.filter((g) => g.by === other) ?? [];
  const selfName = lookOf(world, self).name;
  const mine = reportOf(world, self)?.memberships ?? [];
  const shared = report.memberships.filter((m) => mine.some((x) => x.bloc === m.bloc));

  return (
    <section class="wc-dip-mid wc-scroll wc-fade">
      <RelationBlock rel={rel} />
      <WarLinks wars={wars} />

      <h3 class="wc-rule-title">Shared memberships</h3>
      {shared.length === 0 ? <div class="wc-empty">None at this date</div> : null}
      <div class="wc-members wc-dip-shared">
        {shared.map((m) => (
          <span class="wc-member" key={m.bloc}>
            <b>{m.bloc}</b>
            {m.status !== 'member' ? <i> · {m.status}</i> : null}
            <SourceChip ids={[...new Set([...m.sources, ...(mine.find((x) => x.bloc === m.bloc)?.sources ?? [])])]} />
          </span>
        ))}
      </div>

      <RelationStrip iso3={other} />

      <h3 class="wc-rule-title">Grievance ledger</h3>
      {ledger === null ? (
        <div class="wc-empty">Sanctions and tariffs are not provided by the services layer.</div>
      ) : (
        <div class="wc-ledger">
          <LedgerSide title={`By ${selfName}`} items={byMe} />
          <LedgerSide title={`By ${report.country.name}`} items={byThem} />
        </div>
      )}
    </section>
  );
}

// A non-state party: the relation with its reason, and the wars it fights in. No memberships,
// relation history or grievance ledger: those are country records it does not have.
function PartyRelation({ party }: { party: Party }) {
  return (
    <section class="wc-dip-mid wc-scroll wc-fade">
      <RelationBlock rel={party.relation} />
      <WarLinks wars={party.wars} />
    </section>
  );
}

function PartyColumn({ party }: { party: Party }) {
  const { world } = useServices();
  const kind = PARTY_KIND_TEXT[actorKind(world, party.id) ?? ''];
  const keys = uniqueKeys(party.statements.map(statementBase));

  return (
    <section class="wc-dip-col wc-dip-col--them">
      <header class="wc-dip-leader">
        <Medallion {...actorEmblem(world, party.id)} size={78} />
        <div>
          <div class="wc-dip-side">Them</div>
          <div class="wc-dip-nation">{party.name}</div>
          {kind ? <div class="wc-dip-role">{kind}</div> : null}
        </div>
      </header>

      <div class="wc-dip-col-body wc-scroll wc-fade">
        <h3 class="wc-rule-title">Statements on wars</h3>
        {party.statements.length === 0 ? <div class="wc-empty">No sourced statements in wars live on this date</div> : null}
        {party.statements.map((s, i) => (
          <StatementCard key={keys[i]} s={s} />
        ))}
      </div>
    </section>
  );
}

function RelationBlock({ rel }: { rel: RelationInfo }) {
  return (
    <div class="wc-dip-rel" style={{ '--rel': RELATION_COLOR[rel.relation] }}>
      <span class="wc-dip-rel-icon">
        <Icon name={relationIcon(rel.relation)} size={34} />
      </span>
      <div class="wc-dip-rel-label">{RELATION_LABEL[rel.relation]}</div>
      <p class="wc-dip-reason">
        {rel.reason} <SourceChip ids={rel.sources} />
      </p>
      <ClaimLabel kind="assessment" />
      <div class="wc-dip-rule">One rule set for every leader, first match wins: war → ally → denounced → friend → neutral.</div>
    </div>
  );
}

function WarLinks({ wars }: { wars: War[] }) {
  const { actions } = useServices();
  if (wars.length === 0) {
    return null;
  }

  return (
    <div class="wc-dip-wars">
      {wars.map((w) => (
        <div key={w.id} class="wc-dip-war">
          <Icon name="war" size={18} />
          <button type="button" class="wc-dip-war-open" onClick={() => actions.openPanel('war', w.id)}>
            <b>{w.name}</b>
            <span class="wc-year"> since {fmtDate(w.from)}</span>
          </button>
          <SourceChip ids={w.sources} />
          <Icon name="chevronRight" size={14} />
        </div>
      ))}
    </div>
  );
}

// The relation month by month since 2000 up to the date shown (world.relationHistory). A stretch
// of one relation is one segment; the latest changes are listed with their reason and sources.
//
//   [ neutral ][ friend ][ ally ▇▇▇▇▇▇ ][ war ▇ ]      ← width = months
//   Mar 2014  Denounced  reason [src]
function RelationStrip({ iso3 }: { iso3: string }) {
  const { world } = useServices();
  let months: RelationMonth[] | null = null;
  try {
    months = relationHistory(world, iso3);
  } catch {
    months = null;
  }
  if (!months || months.length === 0) {
    return null;
  }

  const runs = relationRuns(months);
  const last = runs[runs.length - 1];
  const changes = runs.slice(-HISTORY_CHANGES_MAX).reverse();
  const kinds = [...new Set(runs.map((r) => r.relation))].sort((a, b) => RELATION_RANK[a] - RELATION_RANK[b]);
  return (
    <div class="wc-relstrip">
      <h3 class="wc-rule-title">Relation since {fmtMonth(months[0].month)}</h3>
      <div class="wc-relstrip-bar">
        {runs.map((r) => (
          <span
            key={r.from}
            class={r === last ? 'wc-relstrip-seg wc-relstrip-seg--now' : 'wc-relstrip-seg'}
            style={{ background: RELATION_COLOR[r.relation], flexGrow: r.months }}
            title={`${fmtMonth(r.from)}${r.to === r.from ? '' : ` to ${fmtMonth(r.to)}`}: ${RELATION_LABEL[r.relation]}`}
          />
        ))}
      </div>
      <div class="wc-relstrip-axis">
        <span>{fmtMonth(months[0].month)}</span>
        <span class="wc-relstrip-keys">
          {kinds.map((k) => (
            <span key={k}>
              <i style={{ background: RELATION_COLOR[k] }} />
              {RELATION_LABEL[k]}
            </span>
          ))}
        </span>
        <span>{fmtMonth(months[months.length - 1].month)}</span>
      </div>
      <ul class="wc-relstrip-changes">
        {changes.map((r) => (
          <li key={r.from}>
            <b style={{ color: RELATION_COLOR[r.relation] }}>{RELATION_LABEL[r.relation]}</b>
            <span class="wc-year"> {runDates(r, r === last ? 'current' : 'past', months[0].month)}</span>
            <span class="wc-relstrip-why">
              {r.reason} <SourceChip ids={r.sources} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LedgerSide({ title, items }: { title: string; items: Grievance[] }) {
  const keys = uniqueKeys(items.map((g) => g.kind + g.title + g.from));
  return (
    <div class="wc-ledger-side">
      <div class="wc-ledger-title">{title}</div>
      {items.length === 0 ? <div class="wc-empty">None at this date</div> : null}
      {items.map((g, i) => (
        <div class={g.to ? 'wc-grievance wc-grievance--ended' : 'wc-grievance'} key={keys[i]}>
          <Icon name={GRIEVANCE_ICON[g.kind]} size={16} />
          <div class="wc-grievance-text">
            <b>{g.title}</b>
            <span class="wc-year">
              {g.scope ? `${g.scope} · ` : ''}
              {fmtDate(g.from)} – {g.to ? fmtDate(g.to) : STILL_ON[g.kind]}
            </span>
          </div>
          <SourceChip ids={g.sources} />
        </div>
      ))}
    </div>
  );
}
